
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import KeyManagersAutocomplete from "@/components/keys/managers/KeyManagersAutocomplete.vue";
import SimpleDialog from "@/components/SimpleDialog.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiKey, mdiKeyChange, mdiPlus } from "@mdi/js";
import debounce from "debounce";
import KeyAddManagersDialog from "@/components/keys/managers/KeyAddManagersDialog.vue";
import { ROWS_PER_PAGE } from "@/utils/constants";

export const DEBOUNCE_TIME_MILLI: number = 1000;

@Component({
  components: {
    KeyAddManagersDialog,
    SimpleDialog,
    KeyManagersAutocomplete,
    SvgIcon,
  },
})
export default class KeyManagersTable extends Vue {

  get keyManagersHeaders(): any[] {
    const headers: any[] = [{
      value: "name",
      text: this.type === "user" ? this.$tc("key.userCount") : this.$t("user.groups"),
      sortable: false,
    }];
    if (this.canEditManagers) {
      headers.push({
        value: "action",
        sortable: false,
      });
    }
    return headers;
  }

  @Prop({default: null})
  public keyId: number;

  @Prop({default: null})
  public keyGroupId: number;

  @Prop({default: false})
  public canEditManagers: boolean;

  @Prop({default: null})
  public type: "user"|"subordinates";

  @Watch("search")
  public handlerSearch = debounce(this.fetchKeyManagers, DEBOUNCE_TIME_MILLI);

  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private isMobile: boolean;

  private keyManagers: any[] = [];
  private keyManagersCount: number = 0;
  private loadingKeyManagers: boolean = false;
  private search: string = "";
  private pagination: any = {
    page: 1,
    itemsPerPage: 10,
  };
  private addManagerDialog: boolean = false;
  private detailedManager: any = null;
  private keyManagementsDialog: boolean = false;
  private keyManagements: any[] = [];
  private keyManagementsHeaders: object[] = [];
  private removeKeyManagementDialog: boolean = false;
  private loadingRemoveKeyManagement: boolean = false;
  private removedKeyManagement: any = null;

  private icons = {
    mdiKey,
    mdiKeyChange,
    mdiPlus,
  };

  private ROWS_PER_PAGE = ROWS_PER_PAGE;

  @Watch("pagination", {deep: true})
  public handlerPagination(val, oldVal) {
    if (val.page !== oldVal.page || val.itemsPerPage !== oldVal.itemsPerPage) {
      this.fetchKeyManagers();
    }
  }

  private async mounted(): Promise<void> {
    await this.reset();
  }

  private async reset(): Promise<void> {
    this.addManagerDialog = false;
    this.keyManagers = [];
    this.keyManagersCount = 0;
    this.keyManagementsHeaders = [
      { value: "userGroupName", text: this.$t("KeyManagement.userOrGroup"), sortable: false },
      { value: "keyGroupName", text: this.$t("KeyManagement.keyOrGroup"), sortable: false },
      {
        value: "action",
        sortable: false,
        text: this.isMobile ? this.$tc("key.action") : "",
        width: "70px",
      },
    ];
    await this.fetchKeyManagers();
  }

  private async fetchKeyManagers(): Promise<void> {
    this.loadingKeyManagers = true;
    const module = this.keyId ? "keys" : "keyGroups";
    const action = this.type === "user" ? "getKeyManagersSoloById" : "getKeyManagersGroupsById";
    const payload = {
      id: this.keyId || this.keyGroupId,
      pagination: {
        ...this.pagination,
        search: this.search,
      },
    };
    return this.$store.dispatch(`${module}/${action}`, payload)
      .then((res) => {
        this.keyManagers = res?.rows || [];
        this.keyManagersCount = res?.count || 0;
      })
      .finally(() => {
        this.loadingKeyManagers = false;
        this.$emit("loaded");
      });
  }

  private async openKeyManagementsDialog(entity): Promise<void> {
    this.keyManagementsDialog = true;
    this.detailedManager = entity;
    await this.getKeyManagements();
  }

  private closeKeyManagementsDialog(): void {
    this.keyManagementsDialog = false;
    this.keyManagements = [];
  }

  private async getKeyManagements(): Promise<void> {
    await this.$store.dispatch("keyManagements/getByEntities", {
      keyId: this.keyId,
      keyGroupId: this.keyGroupId,
      userId: this.type === "user" ? this.detailedManager.id : undefined,
      userGroupId: this.type === "subordinates" ? this.detailedManager.id : undefined,
    })
      .then((data) => {
        this.keyManagements = data;
      })
      .catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      });
  }

  private openRemoveKeyManagementDialog(item): void {
    this.removedKeyManagement = item;
    this.removeKeyManagementDialog = true;
  }

  private closeRemoveKeyManagementDialog(): void {
    this.removedKeyManagement = null;
    this.removeKeyManagementDialog = false;
  }

  private async removeKeyManagement(): Promise<void> {
    this.loadingRemoveKeyManagement = true;
    await this.$store.dispatch("keyManagements/deleteById", {
      keyManagementId: this.removedKeyManagement.id,
    }).then(async () => {
      this.closeRemoveKeyManagementDialog();
      await this.getKeyManagements();
      if (this.keyManagements.length === 0) {
        this.closeKeyManagementsDialog();
        await this.fetchKeyManagers();
      }
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    }).finally(() => {
      this.loadingRemoveKeyManagement = false;
    });
  }

  private getUserOrGroupName = (userGroup): string => userGroup.name || userGroup.Users[0].displayName;

  private getKeyOrGroupName = (keyGroup): string => keyGroup.name || keyGroup.Keys[0].name;

  private clickRow(item) {
    const route = this.type === "user" ? "user" : "user-group";
    return this.$router.push({name: route, params: {id: item.id}});
  }
}
